/**
 * VIVANA × IT GEEKS — Product-grid variant guard
 *
 * A product card carries no variant picker, so its add-to-cart form can only
 * submit `product.selected_or_first_available_variant`. For a product that still
 * needs a real choice (Collagen Pro's flavour, and any bundle/system product with
 * its own flavour option) that means clicking "Add to cart" in the grid silently
 * adds the default flavour — the shopper never gets to pick.
 *
 * Liquid marks those cards in `snippets/product-card.liquid`:
 *
 *   <product-card data-vivana-requires-choice="true" data-vivana-product-url="…">
 *
 * …using the structural rule in `snippets/vivana-variant-choice-required.liquid`
 * (the same "2+ selectable option values" rule the PDP enforces in
 * `assets/vivana-variant-required.js`). This controller turns the card's add-to-cart
 * controls into a trip to that product page, where the mandatory-selection flow
 * takes over. Products with nothing to choose are untouched — they keep Horizon's
 * native add-to-cart exactly as before.
 *
 * Why delegation on `document` rather than rewriting each button: product grids
 * are re-rendered constantly by the Section Rendering API (facets, sorting,
 * infinite scroll, grid density), and a delegated listener survives every one of
 * those re-renders with no MutationObserver tax.
 *
 * `preventDefault()` on the click is what actually stops the add: the button is a
 * `type="submit"`, so cancelling the click cancels the form submission Horizon's
 * `ProductFormComponent.handleSubmit` listens for. The `submit` listener below is
 * a backstop for non-click submits (Enter key inside the form).
 *
 * Owned file — safe across Horizon updates. Loaded (deferred) from theme.liquid.
 */

(function () {
  // The tag may be emitted more than once (or re-executed by an app); bind once.
  if (window.__vivanaCardVariantRedirect) return;
  window.__vivanaCardVariantRedirect = true;

  var CARD_SELECTOR = '[data-vivana-requires-choice="true"]';

  // Controls that would put the DEFAULT variant in the cart. `.quick-add__button--choose`
  // is deliberately excluded: it opens Horizon's quick-add modal, which renders a
  // real variant picker, so it is already a valid way to choose.
  var ADD_SELECTOR = [
    'button[name="add"]',
    '[ref="addToCartButton"]',
    '.add-to-cart-button',
  ].join(',');

  /**
   * The flagged card ancestor of a node, if any.
   * @param {Element | null} node
   * @returns {Element | null}
   */
  function flaggedCard(node) {
    if (!node || typeof node.closest !== 'function') return null;
    return node.closest(CARD_SELECTOR);
  }

  /**
   * True when the shopper has actually made their choice on the card itself.
   *
   * A card can carry a swatch picker (`blocks/swatches.liquid`), and selecting a
   * swatch dispatches a VariantUpdateEvent that `ProductFormComponent` uses to
   * rewrite the form's `input[name="id"]` (assets/product-form.js) — so the add is
   * for the chosen variant, not the default, and must be left alone. This mirrors
   * the add-vs-choose rule Horizon already applies in `snippets/quick-add.liquid`
   * (a selected variant means "add", no selection means "choose").
   *
   * With no swatch picker on the card there is no way to choose, so every add is a
   * default-variant add and gets redirected.
   *
   * @param {Element} card
   */
  function hasCardSelection(card) {
    var picker = card.querySelector('swatches-variant-picker-component, product-swatches');
    return !!picker && !!picker.querySelector('input:checked');
  }

  /**
   * Send the shopper to the product page so they can pick their variant.
   *
   * Deliberately holds NO "already navigating" state. An earlier version latched a
   * module-level flag on the first redirect and never cleared it, which made the
   * button inert from then on: the click handler cancels the add BEFORE calling
   * this, so a stuck latch meant no redirect AND no add-to-cart. The flag outlived
   * the navigation in two everyday cases — a bfcache restore (the shopper presses
   * Back; this storefront has no beforeunload/unload handler, and `cart-icon.js`
   * re-syncs on `pageshow`+`persisted`, so restores plainly happen), and a second
   * card clicked while the first navigation was still in flight.
   *
   * Guarding against a double `location.assign()` was never worth state anyway:
   * calling it twice is harmless, and the later call winning is the correct
   * outcome — the shopper lands on the card they clicked last.
   *
   * @param {Element} card
   */
  function goToProduct(card) {
    var url = card.getAttribute('data-vivana-product-url');
    if (!url) return;
    window.location.assign(url);
  }

  document.addEventListener(
    'click',
    function (event) {
      var target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      var control = target.closest(ADD_SELECTOR);
      if (!control) return;
      if (control.classList.contains('quick-add__button--choose')) return;

      var card = flaggedCard(control);
      if (!card || hasCardSelection(card)) return;

      // Cancel the add before anything else can act on it, then navigate.
      event.preventDefault();
      event.stopImmediatePropagation();
      goToProduct(card);
    },
    true
  );

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target instanceof Element ? event.target : null;
      if (!form || typeof form.matches !== 'function') return;
      if (!form.matches('form[action*="/cart/add"]')) return;

      var card = flaggedCard(form);
      if (!card || hasCardSelection(card)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      goToProduct(card);
    },
    true
  );
})();
